import { useQuery } from "@tanstack/react-query";
import {
  GET_CASE_SUMMARY_QUERY_KEY,
  TIMER,
  VOICE_NOTE,
  VOICE_NOTE_REVISION_LOG,
} from "../types/CommonTypes";
import { fetcher } from "../../../utils/Axios";
import {
  GET_CASE_SUMMARY_REQUEST,
  UPDATE_MILESTONE_REVISION_REQUEST,
  UPDATE_OPTIONAL_MILESTONE_REVISION_REQUEST,
} from "../types/RequestTypes";
import { GET_CASE_SUMMARY_RESPONSE } from "../types/ResponseTypes";
import { queryClient } from "../../../utils/ReactQueryConfig";
import { DATE_TYPE } from "../../../utils/Types";

async function getCaseSummary(
  reqBody: GET_CASE_SUMMARY_REQUEST
): Promise<GET_CASE_SUMMARY_RESPONSE> {
  const { caseId } = reqBody ?? {};
  const { data } = await fetcher({
    url: `analytics/case-summary/${caseId}`,
  });

  return data;
}

export default function useGetCaseSummaryQuery(caseId?: string) {
  return useQuery({
    queryKey: [GET_CASE_SUMMARY_QUERY_KEY, caseId],
    queryFn: () =>
      getCaseSummary({
        caseId: caseId,
      }),
    enabled: !!caseId,
  });
}

export const updateCaseSummaryCommentInCache = (updateData: {
  caseId: number;
  comments: string;
}) => {
  const { caseId, comments } = updateData;
  queryClient.setQueryData(
    [GET_CASE_SUMMARY_QUERY_KEY, caseId.toString()],
    (oldData?: GET_CASE_SUMMARY_RESPONSE) => {
      if (oldData) {
        const newData = {
          ...oldData,
          comments,
        };
        return newData;
      }
      return oldData;
    }
  );
};

export const updateCaseSummaryVoiceNoteInCache = (updateData: {
  caseId: number;
  data: Partial<VOICE_NOTE>;
}) => {
  const buildVoiceNoteRevisionLogs = (
    note: VOICE_NOTE,
    data: Partial<VOICE_NOTE>,
  ): Array<VOICE_NOTE_REVISION_LOG> | undefined => {
    if (Array.isArray(data.revisionLogs)) {
      return data.revisionLogs;
    }

    const nextNote = data.note;
    const hasNoteChanged =
      typeof nextNote === "string" && nextNote !== note.note;

    if (!hasNoteChanged) {
      return note.revisionLogs;
    }

    const createdAt = data.updatedAt ?? new Date().toISOString();
    const existingLogs = note.revisionLogs ?? [];

    return [
      {
        id: `${note.id}-${createdAt}`,
        caseNoteId: note.id,
        caseId: note.caseId,
        previousNote: note.note,
        revisedNote: nextNote,
        action: "UPDATE",
        revisedByUserId: data.loggedById ?? note.loggedById,
        revisedByUserName: data.loggedBy ?? note.loggedBy,
        createdAt,
      },
      ...existingLogs,
    ];
  };

  const { caseId, data } = updateData;
  queryClient.setQueryData(
    [GET_CASE_SUMMARY_QUERY_KEY, caseId.toString()],
    (oldData?: GET_CASE_SUMMARY_RESPONSE) => {
      if (oldData) {
        const newData = {
          ...oldData,
          caseNotes: oldData?.caseNotes?.map((note) =>
            note?.id === data?.id
              ? {
                  ...note,
                  ...data,
                  revisionLogs: buildVoiceNoteRevisionLogs(note, data),
                }
              : note
          ),
        };
        return newData;
      }
      return oldData;
    }
  );
};

export const updateCaseSummaryTimerLabelInCache = (updateData: {
  caseId: number;
  data: Partial<TIMER>;
}) => {
  const { caseId, data } = updateData;
  queryClient.setQueryData(
    [GET_CASE_SUMMARY_QUERY_KEY, caseId.toString()],
    (oldData?: GET_CASE_SUMMARY_RESPONSE) => {
      if (oldData) {
        const newData = {
          ...oldData,
          timers: oldData?.timers?.map((timer) =>
            timer?.id === data?.id ? { ...timer, ...data } : timer
          ),
        };
        return newData;
      }
      return oldData;
    }
  );
};

export const appendRevisionToCaseSummaryCache = (updateData: {
  isOptionalMilestone?: boolean;
  revision:
    | (UPDATE_MILESTONE_REVISION_REQUEST & {
        optionalMilestoneId?: number;
      })
    | (UPDATE_OPTIONAL_MILESTONE_REVISION_REQUEST & {
        milestoneId?: number;
        milestoneStartTime?: DATE_TYPE;
      });
}) => {
  const { isOptionalMilestone } = updateData;
  const { caseId, ...revisionData } = updateData?.revision ?? {};
  queryClient.setQueryData(
    [GET_CASE_SUMMARY_QUERY_KEY, caseId.toString()],
    (oldData?: GET_CASE_SUMMARY_RESPONSE) => {
      if (!oldData?.milestoneRevisions) {
        return oldData;
      }

      const revisionLog = isOptionalMilestone
        ? {
            id: revisionData.optionalMilestoneId,
            milestoneId: revisionData.optionalMilestoneId,
            milestoneUUID: revisionData.milestoneUUID,
            milestoneRevisedByUserId: revisionData.milestoneRevisedByUserId,
            milestoneRevisedByUserName: revisionData.milestoneRevisedByUserName,
            milestoneRevisedEndTime: revisionData.milestoneEndTime,
            action: null,
            endTimeLoggedBy: null,
            comment: revisionData.comment,
            createdAt: revisionData.createdAt,
            updatedAt: revisionData.createdAt,
          }
        : {
            id: revisionData.milestoneId,
            milestoneId: revisionData.milestoneId,
            milestoneUUID: revisionData.milestoneUUID,
            milestoneRevisedByUserId: revisionData.milestoneRevisedByUserId,
            milestoneRevisedByUserName: revisionData.milestoneRevisedByUserName,
            milestoneRevisedStartTime: revisionData.milestoneStartTime,
            milestoneRevisedEndTime: revisionData.milestoneEndTime,
            action: revisionData.action,
            startTimeLoggedBy:
              revisionData.action === "start"
                ? revisionData.milestoneRevisedByUserName
                : undefined,
            endTimeLoggedBy:
              revisionData.action === "end"
                ? revisionData.milestoneRevisedByUserName
                : undefined,
            comment: revisionData.comment,
            createdAt: revisionData.createdAt,
            updatedAt: revisionData.createdAt,
          };

      const revisions = (
        isOptionalMilestone
          ? oldData.optionalMilestones
          : oldData.milestoneRevisions
      ).map((milestone) =>
        milestone?.milestoneUuid === revisionData.milestoneUUID
          ? {
              ...milestone,
              milestoneRevisionLogs: [
                ...milestone.milestoneRevisionLogs,
                revisionLog,
              ],
              skipped: false,
              skipReason: undefined,
              skipReasonNote: undefined,
            }
          : milestone
      );

      // Create immutable update without deep cloning
      return isOptionalMilestone
        ? {
            ...oldData,
            optionalMilestones: revisions,
          }
        : {
            ...oldData,
            milestoneRevisions: revisions,
          };
    }
  );
};
